import { I18n } from "@assets/strings";
import { List, Range } from "immutable";
import React, { useEffect, useState } from "react";
import { ResizePayload, useResizeDetector } from "react-resize-detector";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { useMyAssets } from "@domain/media/hooks";
import { allAssetTypes, AssetType, Media } from "@domain/media/media";
import { isImage } from "@domain/media/utils";
import { useDebouncedSearch } from "@domain/search/hook";
import { useIsMobile } from "@utils/hooks";
import { mobile } from "@ui/styles/utils";
import { Secondary } from "@ui/components/containers/textBlocks";
import { Subtitle } from "@ui/components/containers/title";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Selecter } from "@ui/components/inputs/selecter";
import { row, stack } from "@ui/components/layout";
import { SearchBar } from "@ui/components/search/searchBar";
import { AssetView, AssetViewSkeleton } from "@ui/components/media/assetView";
import { ZoomedAsset } from "@ui/components/media/zoomedAsset";

const MOBILE_GAP = 11;
const DESKTOP_GAP = 24;
const DESKTOP_THUMBNAIL_MIN_SIZE = 160;
const MOBILE_THUMBNAIL_MIN_SIZE = 120;
interface AssetManagerProps extends React.HTMLAttributes<HTMLDivElement> {
	showTitle?: boolean;
	selectedAssets?: List<Media>;
	onAssetsSelected?: (assets: List<Media>) => void;
	assetDisabler?: (asset: Media) => boolean;
	multiSelectEnabled?: boolean;
}
export const AssetManager: React.FC<AssetManagerProps> = ({
	showTitle,
	selectedAssets,
	onAssetsSelected,
	assetDisabler,
	multiSelectEnabled,
	...htmlProps
}) => {
	const { mediaService } = useServices();
	const { value, query, onChange } = useDebouncedSearch();
	const [assetType, setAssetType] = useState<AssetType | undefined>();
	const { result: assets, loading, totalElements, fetchMore } = useMyAssets(query, assetType);
	const isMobile = useIsMobile();
	const [rowCount, setRowCount] = useState(isMobile ? 1 : 2);
	const [galleryContent, setGalleryContent] = useState<Media | null>(null);

	const multiSelectingImages = multiSelectEnabled && !!selectedAssets?.find(isImage);

	useEffect(() => {
		const isInAsset = (target: Element): boolean => {
			if (target.getAttribute("data-asset")) {
				return true;
			}
			const parent = target.parentElement;
			return parent ? isInAsset(parent) : false;
		};
		const handleClickOutside = (event: MouseEvent): void => {
			const target = event.target as Element | null;
			if (!target || !isInAsset(target)) {
				onAssetsSelected?.(List());
			}
		};
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	const { ref } = useResizeDetector({
		handleHeight: false,
		onResize: (payload: ResizePayload) =>
			setRowCount(
				Math.floor(
					(payload.width ?? 0) /
						(isMobile ? MOBILE_GAP + MOBILE_THUMBNAIL_MIN_SIZE : DESKTOP_GAP + DESKTOP_THUMBNAIL_MIN_SIZE)
				)
			),
	});

	return (
		<>
			{galleryContent && <ZoomedAsset asset={galleryContent} onClose={() => setGalleryContent(null)} />}
			<Container {...htmlProps} ref={ref}>
				{showTitle && <Subtitle>{I18n.t("asset_manager.files.count", { count: totalElements ?? 0 })}</Subtitle>}
				<Filters>
					<StyledSearchBar
						containerStyle={{ flex: 1 }}
						value={value}
						onChange={onChange}
						placeholder={I18n.t("asset_manager.search.placeholder")}
					/>
					<StyledSelecter
						light
						value={assetType ?? "ALL"}
						options={[
							{ value: "ALL", label: I18n.t("asset.type.all") },
							...allAssetTypes.map((type) => ({ value: type, label: I18n.t(`asset.type.${type}`) })),
						]}
						onChange={(v) => {
							setAssetType(v === "ALL" ? undefined : (v as AssetType));
						}}
					/>
				</Filters>
				<div style={{ overflow: "scroll" }}>
					<InfiniteScroll loading={loading} onEndReached={fetchMore}>
						<FileGrid rowCount={assets.length === 0 && !loading ? 1 : rowCount}>
							{assets.length > 0 ? (
								assets.map((asset) => (
									<SelectableAssetView
										selected={!!selectedAssets?.find(({ id }) => asset.id === id)}
										disabled={assetDisabler?.(asset)}
										resetSelection={multiSelectingImages && !isImage(asset)}
										onClick={
											onAssetsSelected
												? () => {
														const assetIndex = selectedAssets?.findIndex(({ id }) => asset.id === id) ?? -1;
														onAssetsSelected(
															!!selectedAssets && assetIndex >= 0
																? selectedAssets.remove(assetIndex)
																: !!selectedAssets && multiSelectingImages && isImage(asset)
																	? selectedAssets.push(asset)
																	: List([asset])
														);
													}
												: () => setGalleryContent(asset)
										}
										onDelete={() => mediaService.delete(asset.id, query, assetType)}
										key={asset.id}
										asset={asset}
										cursor={onAssetsSelected ? "pointer" : "zoom-in"}
									/>
								))
							) : loading ? (
								Range(0, 9).map((i) => <AssetViewSkeleton key={i} />)
							) : (
								<Secondary>
									{I18n.t(query || assetType ? "asset_manager.search.empty" : "asset_manager.empty")}
								</Secondary>
							)}
						</FileGrid>
					</InfiniteScroll>
				</div>
			</Container>
		</>
	);
};

const Container = styled.div`
	${stack(40, "flex-start", "stretch")};

	@media ${mobile} {
		${stack(20, "flex-start", "stretch")};
	}
`;

const FileGrid = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "rowCount",
})<{ rowCount: number }>`
	display: grid;
	grid-template-columns: repeat(${({ rowCount }) => rowCount}, 1fr);
	grid-row-gap: ${DESKTOP_GAP}px;
	grid-column-gap: ${DESKTOP_GAP}px;
	@media ${mobile} {
		grid-row-gap: ${MOBILE_GAP}px;
		grid-column-gap: ${MOBILE_GAP}px;
	}
`;

const SelectableAssetView = styled(AssetView)<{
	cursor?: "zoom-in" | "pointer";
	selected?: boolean;
	disabled?: boolean;
	resetSelection?: boolean;
}>`
	> :first-child {
		${({ cursor }) => `cursor: ${cursor ?? "initial"}`};
	}
	${({ selected, theme }) =>
		selected &&
		css`
			> :first-child {
				border: 1px solid transparent;
				&::after {
					content: "";
					position: absolute;
					top: 0;
					right: 0;
					bottom: 0;
					left: 0;
					border-radius: 13px;
					box-shadow: inset 0 0 0 3px ${theme.colors.secondary};
				}
			}
		`};
	${({ resetSelection }) => resetSelection && "opacity: 0.5"};
	${({ disabled }) =>
		disabled &&
		css`
			opacity: 0.4;
			pointer-events: none;
		`};
`;

const Filters = styled.div`
	${row(10)}
	@media ${mobile} {
		${row(6)}
	}
`;

const StyledSelecter = styled(Selecter)`
	flex-shrink: 0;
`;

const StyledSearchBar = styled(SearchBar)`
	.input-wrapper {
		border-radius: 13px;
	}
`;
